<template>
    <ai-popup
        ref="popupRef"
        :title="popupTitle"
        :bottom-buttons-enabled="true"
        :first-button-disabled="disablePopupFirstButton"
        :is-explicit-close="false"
        width="80vw"
        height="80vh"
        v-model:visible="state.rolePopupVisible"
        @submit-click="onSaveHandler"
        @close-popup="closePopup"
    >
        <div class="flex flex-col p-2 gap-3 h-full">
            <description-form ref="descriptionRef" :read-only="isSystemRole" />
            <areas-grid
                ref="areasGridRef"
                v-if="state.isDataLoaded"
                @saveData="onSaveHandler"
                @restartWatching="restartWatching"
            />
        </div>
    </ai-popup>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import AiPopup from '@/components/ai-popup/ai-popup.vue';
import AreasGrid from '@/views/administration/roles/areas-grid.vue';
import DescriptionForm from '@/views/administration/roles/description-form.vue';
import { dxUiNotify } from '@/utils/dx-helper.js';
import { showLoadPanel, hideLoadPanel } from '@/components/utils/ai-global-load-panel.js';
import AiCustomConfirmDialog from '@/utils/ai-custom-confirm-dialog.js';
import roleLocalStore from '@/views/administration/roles/store.js';
import enviromentData from '@/store/enviroment-data.js';

const { state, MODE, UI_ELEMENTS_IDS, clearRoleData, loadById, isNotSystemRole } = roleLocalStore;
const { disableUiElement } = enviromentData;

const isSystemRole = !isNotSystemRole();
const areasGridRef = ref();
const descriptionRef = ref();
const validateCallback = ref();
const validateCodeCallback = ref();
const popupRef = ref();
const popupTitle = computed(() => state.mode === MODE.UPDATE ? `Роль: ${state.formData.name}` : 'Создание роли');
const emit = defineEmits(['restartWatching', 'refreshGridData']);
const isDisableUiElement = disableUiElement(UI_ELEMENTS_IDS.CARD);

onMounted(() => {
    validateCodeCallback.value = descriptionRef.value.validateUniqueCode;
    validateCallback.value = descriptionRef.value.formValidate;
});

const disablePopupFirstButton = computed(() =>
    isDisableUiElement ||
    isSaveReady.value ||
    isSystemRole ||
    state.requestBodyHasNotChanged,
);

const restartWatching = () => {
    emit('restartWatching');
};

const stopWatching = () => {
    if (!isSystemRole) state.stopWatching();
    state.requestBodyHasNotChanged = true;
};

const isSaveReady = computed(() => !(state.formData.name && state.formData.code && state.areas.length));

const onSaveHandler = async () => {
    try {
        showLoadPanel();
        const isCodeValid = await validateCodeCallback.value();
        const validateForm = await validateCallback.value();

        const isAreasNotEmpty = state.areas.filter(area => !area.isPreparedToRemove).length;
        if (!isAreasNotEmpty) {
            dxUiNotify('Нельзя сохранить роль без областей использования', 'error');
            return;
        }
        if (isCodeValid && validateForm.isValid) {
            const appIds = state.mode === MODE.UPDATE ? state.appIds : [state.formData.appId];
            const { refreshRolesGrid } = await areasGridRef.value.saveData();
            await loadById(state.id, appIds);
            if (refreshRolesGrid) {
                state.refreshRolesGrid = true;
                emit('restartWatching');
            }
        }
    } finally {
        hideLoadPanel();
    }
};

const showSaveDataConfirmDialog = async () => {
    const result = await AiCustomConfirmDialog('Сохранить изменения?', 'Запрос на сохранение');
    if (result === -1) return false;
    else if (result === 1) await onSaveHandler();
    return true;
};

const refreshGridData = () => {
    emit('refreshGridData');
};

const closePopup = async () => {
    if (state.requestBodyHasNotChanged) {
        stopWatching();
        clearRoleData();
        state.rolePopupVisible = false;
        if (state.refreshRolesGrid) refreshGridData();
        state.refreshRolesGrid = false;
        return;
    }
    const result = await showSaveDataConfirmDialog();
    if (state.refreshRolesGrid) refreshGridData();
    state.refreshRolesGrid = false;

    if (result) {
        stopWatching();
        if (!state.areaPopupVisible) clearRoleData();
        state.formData = {};
        state.rolePopupVisible = false;
    }
};
</script>
